/**
 * 1.3.0 Administration → Media portal → Branding and Settings (P1).
 *  - Branding (1.3.0 part): the portal's name, welcome text, help text and the rights declaration (its version goes
 *    up with each change of the text). The logo, accent colour and email texts are 1.3.1's.
 *  - Settings: on or off, the address (PRISM_PORTAL_ORIGIN), two-step policy, default acceptance, the largest
 *    upload, the intake storage location, retention, reminder times (stored; 1.3.1 sends them) and ClamAV's status.
 */
import { useEffect, useState } from 'react';
import { Palette, Save, Settings2, ShieldCheck } from 'lucide-react';
import { t, type AcceptanceMode, type PortalSettingsDTO, type PortalSettingsPatch, type TwoStepPolicy } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalChoices, usePortalSettings } from '../../../api/hooks/portalAdmin';
import { toast } from '../../../components/Toasts';
import { relative } from '../../../lib/format';
import { AcceptanceOptions, fromGib, toGib } from './shared';
import { PortalLogo } from './Logo';
import { PortalEmailTexts } from './EmailTexts';
import { PortalReminderSettings } from './ReminderSettings';

function useSave() {
  const refresh = usePortalAdminRefresh();
  const [busy, setBusy] = useState(false);
  const save = async (patch: PortalSettingsPatch, done: string) => {
    setBusy(true);
    try {
      await api<PortalSettingsDTO>('/api/v1/admin/portal/settings', { method: 'PATCH', json: patch });
      await refresh();
      toast(done);
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return { busy, save };
}

export function PortalBranding() {
  const s = usePortalSettings().data;
  const { busy, save } = useSave();
  const [v, setV] = useState({ portalName: '', welcomeText: '', helpText: '', rightsText: '' });
  useEffect(() => {
    if (s) setV({ portalName: s.branding.portalName ?? '', welcomeText: s.branding.welcomeText, helpText: s.branding.helpText, rightsText: s.rightsText });
  }, [s]);
  if (!s) return <p className="muted">Loading…</p>;
  const rightsChanged = v.rightsText !== s.rightsText;
  const dirty = rightsChanged || v.portalName !== (s.branding.portalName ?? '') || v.welcomeText !== s.branding.welcomeText || v.helpText !== s.branding.helpText;
  return (
    <section className="panel-card portal-form" data-testid="portal-branding">
      <h3><Palette size={16} color="var(--accent-strong)" /> Branding</h3>
      <p className="muted portal-lede">What suppliers see on the portal’s sign-in page and in its emails.</p>
      <div className="field">
        <label htmlFor="pb-name">Portal name</label>
        <input id="pb-name" className="input" maxLength={120} value={v.portalName} placeholder={t('admin.portalName.default')} onChange={(e) => setV({ ...v, portalName: e.target.value })} data-testid="portal-branding-name" />
      </div>
      <div className="field">
        <label htmlFor="pb-welcome">Welcome text</label>
        <textarea id="pb-welcome" className="textarea" maxLength={5000} value={v.welcomeText} onChange={(e) => setV({ ...v, welcomeText: e.target.value })} placeholder="Shown above the sign-in form" data-testid="portal-branding-welcome" />
      </div>
      <div className="field">
        <label htmlFor="pb-help">Help for suppliers</label>
        <textarea id="pb-help" className="textarea" maxLength={5000} value={v.helpText} onChange={(e) => setV({ ...v, helpText: e.target.value })} placeholder="Who to contact, delivery hours, technical notes" />
      </div>
      <div className="field">
        <label htmlFor="pb-rights">Rights declaration <span className="pill" data-testid="portal-rights-version">Version {s.rightsVersion}</span></label>
        <textarea id="pb-rights" className="textarea" maxLength={20000} style={{ minHeight: 110 }} value={v.rightsText} placeholder={t('admin.rights.default')} onChange={(e) => setV({ ...v, rightsText: e.target.value })} data-testid="portal-branding-rights" />
        <span className="help">Suppliers confirm it with every submission; who, when and which version is kept with it. {rightsChanged ? `Saving makes it version ${s.rightsVersion + 1}.` : 'Empty: the default text shown above.'}</span>
      </div>
      <div className="portal-save-row">
        <button className="btn primary" disabled={!dirty || busy} onClick={() => void save({ portalName: v.portalName.trim() || null, welcomeText: v.welcomeText, helpText: v.helpText, ...(rightsChanged ? { rightsText: v.rightsText } : {}) }, 'Branding saved')} data-testid="portal-branding-save"><Save size={14} /> Save branding</button>
        <span className="faint">Last changed {relative(s.updatedAt)}</span>
      </div>
      {/* 1.3.1 (Q1): the logo and accent colour, and the email texts. */}
      <PortalLogo />
      <PortalEmailTexts />
    </section>
  );
}

export function PortalSettings() {
  const s = usePortalSettings().data;
  const choices = usePortalChoices().data;
  const { busy, save } = useSave();
  const [v, setV] = useState({ enabled: false, twoStepPolicy: 'required' as TwoStepPolicy, defaultAcceptance: 'review' as AcceptanceMode, maxGib: '', intakeLocationId: '', retentionDays: '30', idleMinutes: '60', reminders: '3, 1' });
  useEffect(() => {
    if (s) setV({
      enabled: s.enabled, twoStepPolicy: s.twoStepPolicy, defaultAcceptance: s.defaultAcceptance, maxGib: toGib(s.maxUploadBytes), intakeLocationId: s.intakeLocationId ?? '',
      retentionDays: String(s.retentionDays), idleMinutes: String(s.idleMinutes), reminders: s.reminderOffsetsDays.join(', '),
    });
  }, [s]);
  if (!s) return <p className="muted">Loading…</p>;
  const reminders = v.reminders.split(/[\s,;]+/).filter(Boolean).map(Number);
  const remindersOk = reminders.every((n) => Number.isInteger(n) && n >= 1 && n <= 60) && reminders.length <= 5;
  const retention = Number(v.retentionDays);
  const idle = Number(v.idleMinutes);
  const idleOk = v.idleMinutes.trim() !== '' && Number.isInteger(idle) && idle >= 0 && idle <= 1440;
  const maxBytes = fromGib(v.maxGib);
  const valid = remindersOk && Number.isInteger(retention) && retention >= 1 && retention <= 3650 && idleOk && maxBytes !== null;
  const patch: PortalSettingsPatch = {
    enabled: v.enabled, twoStepPolicy: v.twoStepPolicy, defaultAcceptance: v.defaultAcceptance, intakeLocationId: v.intakeLocationId || null, retentionDays: retention, idleMinutes: idle,
    reminderOffsetsDays: reminders, ...(maxBytes !== null && toGib(maxBytes) !== toGib(s.maxUploadBytes) ? { maxUploadBytes: maxBytes } : {}),
  };
  const location = choices?.storageLocations.find((l) => l.id === v.intakeLocationId);
  /** 1.3.1: a saved intake location the choices leave out (removed in Storage since). */
  const locationGone = Boolean(choices && v.intakeLocationId && !location);
  return (
    <>
      <section className="panel-card portal-form" data-testid="portal-settings">
        <h3><Settings2 size={16} color="var(--accent-strong)" /> Settings</h3>
        <label className="toggle-row"><input type="checkbox" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} data-testid="portal-enabled" />
          <span><strong>The portal is on</strong><span className="help" style={{ display: 'block' }}>Off: suppliers see “The portal is not open at the moment” and cannot sign in.</span></span></label>
        <div className="field">
          <label>Address</label>
          {s.effectiveOrigin ? <code className="code-block" data-testid="portal-address">{s.effectiveOrigin}</code>
            : <p className="portal-bad" style={{ margin: 0 }}>Not set: the portal answers nowhere until PRISM_PORTAL_ORIGIN is set for the API (and its site in Caddy).</p>}
          <span className="help">From PRISM_PORTAL_ORIGIN. The portal and PRISM never share an address or a session.</span>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ps-2fa">Two-step sign-in</label>
            <select id="ps-2fa" className="select" value={v.twoStepPolicy} onChange={(e) => setV({ ...v, twoStepPolicy: e.target.value as TwoStepPolicy })} data-testid="portal-2fa">
              <option value="required">Required (recommended)</option>
              <option value="optional">Optional</option>
            </select>
            <span className="help">A company can be set apart from this in Companies & users.</span>
          </div>
          <div className="field">
            <label htmlFor="ps-accept">Default acceptance</label>
            <select id="ps-accept" className="select" value={v.defaultAcceptance} onChange={(e) => setV({ ...v, defaultAcceptance: e.target.value as AcceptanceMode })}>
              <AcceptanceOptions />
            </select>
            <span className="help">For delivery specs that do not set their own. Automatic modes act when QC finishes, so they need the spec’s QC profile.</span>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ps-max">Largest upload (GiB)</label>
            <input id="ps-max" className={`input ${maxBytes === null ? 'invalid' : ''}`} inputMode="decimal" value={v.maxGib} onChange={(e) => setV({ ...v, maxGib: e.target.value })} />
            <span className="help">At most {toGib(choices?.uploadMaxBytes)} GiB (PRISM_PORTAL_UPLOAD_MAX_BYTES).</span>
          </div>
          <div className="field">
            <label htmlFor="ps-location">Intake storage location</label>
            <select id="ps-location" className="select" value={v.intakeLocationId} onChange={(e) => setV({ ...v, intakeLocationId: e.target.value })} data-testid="portal-intake-location">
              <option value="">Not chosen: uploads are refused</option>
              {choices?.storageLocations.map((l) => <option key={l.id} value={l.id} disabled={!l.usable}>{l.name} · {l.site}{l.usable ? '' : ' (a storage agent)'}</option>)}
              {locationGone && <option value={v.intakeLocationId} disabled>A removed storage location</option>}
            </select>
            {locationGone
              ? <span className="portal-bad" role="alert" data-testid="portal-intake-location-removed">The saved intake location has been removed in Storage. Choose another one: until then uploads cannot be stored.</span>
              : <span className="help">{location ? 'Pending uploads wait here until they are accepted.' : 'A location of its own keeps pending files apart from the Library’s.'}</span>}
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ps-retention">Keep rejected and withdrawn uploads (days)</label>
            <input id="ps-retention" className="input" inputMode="numeric" value={v.retentionDays} onChange={(e) => setV({ ...v, retentionDays: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="ps-idle">Sign out idle suppliers after (minutes)</label>
            <input id="ps-idle" className={`input ${idleOk ? '' : 'invalid'}`} inputMode="numeric" value={v.idleMinutes} onChange={(e) => setV({ ...v, idleMinutes: e.target.value })} data-testid="portal-idle" />
            <span className="help">0: never. The portal’s own, apart from members’ idle sign-out.</span>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="ps-reminders">Reminders before the due date (days)</label>
            <input id="ps-reminders" className={`input ${remindersOk ? '' : 'invalid'}`} value={v.reminders} onChange={(e) => setV({ ...v, reminders: e.target.value })} />
            <span className="help">Reminder emails go to the suppliers this many days before a delivery is due.</span>
          </div>
        </div>
        <div className="portal-save-row">
          <button className="btn primary" disabled={!valid || busy} onClick={() => void save(patch, 'Portal settings saved')} data-testid="portal-settings-save"><Save size={14} /> Save settings</button>
          <span className="faint">Last changed {relative(s.updatedAt)}</span>
        </div>
      </section>
      <section className="panel-card portal-form portal-section" data-testid="portal-clamav">
        <h3><ShieldCheck size={16} color="var(--accent-strong)" /> Virus scanning (ClamAV)</h3>
        {s.clamav.configured ? (
          <p style={{ margin: 0 }}>
            Configured (PRISM_PORTAL_CLAMAV_URL). {s.clamav.reachable === null ? 'Not checked yet.' : s.clamav.reachable ? <span className="portal-ok">Answering{s.clamav.version ? `: ${s.clamav.version}` : ''}.</span> : <span className="portal-bad">Not answering.</span>}
            {s.clamav.checkedAt && <span className="faint"> Last check {relative(s.clamav.checkedAt)}.</span>}
          </p>
        ) : <p className="portal-bad" style={{ margin: 0 }}>Not configured: portal uploads are not scanned. Set PRISM_PORTAL_CLAMAV_URL (tcp://clamav:3310).</p>}
      </section>
      {/* 1.3.1 (Q2): expiry and the reminders sent. */}
      <PortalReminderSettings />
    </>
  );
}
